import { FunctionalComponent, h, VNode } from '@stencil/core'
import { Fragment, JSXBase } from '@stencil/core/internal'
import { createStore } from '@stencil/store'
import { copyText } from 'src/utils/clipboard'

import { SharedProps } from '../app'
import { PersonAvatar } from '../call/avatar/ui'
import { IconName } from '../icons'
import { InertiaLink } from '../inertia/link/link'
import { IUser } from '../page/admin/types'
import { ColorsType } from '../tailwind'
import { InlineIconText } from '../ui/components'

type InertiaLinkProps = Parameters<typeof InertiaLink>[0]

export const GlobalNavLink: FunctionalComponent<{ color?: ColorsType } & InertiaLinkProps> = (
  { color = 'link', class: className = '', ...attrs },
  children
) => (
  <InertiaLink {...attrs} class={`${className} text-xl uppercase text-${color}`}>
    {children}
  </InertiaLink>
)

export const GlobalNavMenuButton: FunctionalComponent<
  { icon?: IconName; label?: string; newBadge?: boolean; updatedBadge?: boolean } & Parameters<typeof InertiaLink>[0]
> = ({ class: className = '', label, icon, newBadge, updatedBadge, ...attrs }, children) => (
  <InertiaLink
    {...attrs}
    class={`${className} z-menu relative group text-glyph-light leading-none hover:text-link hover:bg-opacity-10 hover:bg-link flex items-center justify-between p-2 text-base font-semibold whitespace-no-wrap rounded-md`}
    activeClass='bg-white bg-opacity-10'
  >
    {icon && <ui-icon icon={icon} size='xl' class='text-glyph-lighter group-hover:text-link group-hover:opacity-50' />}
    <span class='hidden md:inline mr-3px ml-8px'>{label || children}</span>
    {newBadge && (
      <span class='ml-1 text-xs text-surface-base uppercase bg-info-warn rounded-full' style={{ padding: '1px 6px' }}>
        New
      </span>
    )}
    {updatedBadge && (
      <span class='ml-1 text-xs text-surface-base uppercase bg-info-warn rounded-full' style={{ padding: '1px 6px' }}>
        Updated
      </span>
    )}
    {label && children}
  </InertiaLink>
)

export const GlobalNavMenu: typeof GlobalNavMenuButton = (props, children) => (
  <GlobalNavMenuButton {...props} class='text-surface-highest bg-surface-highest'>
    <ui-icon class='-ml-2 w-0 text-xl' icon='three-dots-vertical'></ui-icon>
    <ui-tooltip bgColor='surface-high' borderColor='transparent' persist on='click' anchor='bottom'>
      <div class='px-2'>{children}</div>
    </ui-tooltip>
  </GlobalNavMenuButton>
)

type Header = JSXBase.IntrinsicElements['header']

export const GlobalNavStore = createStore({
  subnav: null as VNode[],
  class: null as Header['class'],
  style: null as Header['style'],
})

/**
 * Basic component which populates/controls the global nav
 */
export const SubNav: FunctionalComponent<Header> = (_props, children) => {
  GlobalNavStore.state.subnav = children.length ? children : null
  return null
}

export const SubNavLink: typeof InertiaLink = ({ class: className, ...attrs }, children) => (
  <InertiaLink
    {...attrs}
    class={`${className} flex items-center h-full px-1 mr-4 text-lg font-medium leading-none hover:text-link`}
    activeClass='text-link border-link pt-2px border-b-2 font-bold'
  >
    {children}
  </InertiaLink>
)

const { state } = createStore({
  showMenu: false,
  showInviteDialog: false,
})

const MenuDivider = () => <div class='-mr-10 border-b border-surface-higher' style={{ marginLeft: '-34px' }} />

const MenuLink: FunctionalComponent<{ icon?: IconName; formUrl?: string } & Parameters<typeof InertiaLink>[0]> = (
  { icon, formUrl, ...linkAttrs },
  children
) => {
  return formUrl ? (
    <form class='flex items-end' method='post' action={formUrl}>
      <csrf-token />
      <button class='flex flex-1 items-end text-left opacity-50 hover:opacity-100'>
        {icon && (
          <ui-icon
            icon={icon}
            class='-mt-2px text-surface-highest'
            style={{ marginLeft: '-20px', marginRight: '7px' }}
          />
        )}
        <span class='flex-1' style={{ marginLeft: !icon ? '-20px' : '0' }}>
          {children}
        </span>
      </button>
    </form>
  ) : (
    <InertiaLink class='flex items-end opacity-50 hover:opacity-100' {...linkAttrs} activeClass='opacity-50'>
      {icon && (
        <ui-icon icon={icon} class='-mt-2px text-surface-highest' style={{ marginLeft: '-20px', marginRight: '7px' }} />
      )}
      <span class='flex-1' style={{ marginLeft: !icon ? '-20px' : '0' }}>
        {children}
      </span>
    </InertiaLink>
  )
}

export const AvatarMenu: FunctionalComponent<{
  adminConfig?: SharedProps['adminConfig']
  logoutUrl: string
  appDownloadUrl?: string
  startTestCallUrl?: string
  tutorialUrl?: string
  currentUser?: IUser
  currentVerifiedEmail?: string
}> = ({ adminConfig, logoutUrl, appDownloadUrl, startTestCallUrl, tutorialUrl, currentUser, currentVerifiedEmail }) => (
  <button
    class='z-menu px-13px -ml-3 h-full hover:bg-glyph-lighter hover:bg-opacity-10'
    onMouseEnter={() => (state.showMenu = true)}
    onMouseLeave={() => (state.showMenu = false)}
  >
    {currentUser ? (
      <PersonAvatar style={{ fontSize: '32px' }} email={currentUser.email} avatarUrl={currentUser.avatar_url} />
    ) : currentVerifiedEmail ? (
      <PersonAvatar style={{ fontSize: '32px' }} email={currentVerifiedEmail} />
    ) : null}
    <menu
      hidden={!state.showMenu}
      class='flex absolute right-0 z-menu flex-col py-12px pr-10 m-0 text-md font-medium text-left bg-surface-high rounded-bl pointer-events-auto spacing-y-12px'
      style={{
        top: '100%',
        paddingLeft: '34px',
        // Crazy shadow from Figma
        boxShadow: `
            0px 44px 34px 0px rgba(0, 0, 0, 0.3),
            0px 40px 65px 0px rgba(0, 0, 0, 0.55),
            0px 4px 4px 0px rgba(0, 0, 0, 0.15);
          `,
      }}
    >
      {currentUser?.admin && adminConfig && !adminConfig.isImpersonating && (
        <Fragment>
          <MenuLink href={adminConfig.adminPointsUrl} icon='coin-star-fill'>
            Points (Admin)
          </MenuLink>
          <MenuDivider />
        </Fragment>
      )}
      {startTestCallUrl && (
        <MenuLink formUrl={startTestCallUrl} icon='camera-video'>
          Start Test Call
        </MenuLink>
      )}
      {tutorialUrl && (
        <MenuLink href={tutorialUrl} icon='slides'>
          Tutorial
        </MenuLink>
      )}
      <MenuLink href='https://help.fathom.video' target='_blank' icon='question-circle-fill'>
        FAQs
      </MenuLink>
      <MenuDivider />
      {adminConfig && adminConfig.isImpersonating && (
        <Fragment>
          <MenuLink formUrl={adminConfig.stopImpersonatingUrl} icon='emoji-sunglasses'>
            Stop Impersonating
          </MenuLink>
          <MenuDivider />
        </Fragment>
      )}
      <MenuLink href='/privacy' target='_blank'>
        Privacy Policy
      </MenuLink>
      <MenuLink href='/terms' target='_blank'>
        Terms of Service
      </MenuLink>
      <MenuLink href='https://security.fathom.video' target='_blank'>
        Security &amp; Compliance
      </MenuLink>
      <MenuLink href='https://status.fathom.video' target='_blank'>
        System Status
      </MenuLink>
      <MenuDivider />
      {!!appDownloadUrl && (
        <MenuLink href={appDownloadUrl} icon='box-arrow-in-down' target='_blank'>
          Download App
        </MenuLink>
      )}
      <MenuLink formUrl={logoutUrl} icon='logout'>
        <input type='hidden' name='_method' value='delete' />
        Logout
      </MenuLink>
      <MenuDivider />
      <div class='-ml-20px cursor-default'>
        <div class='m-0 text-surface-highest' style={{ fontSize: '11px', lineHeight: '13.75px' }}>
          Logged in as
        </div>
        <div class='opacity-50'>{currentUser?.email || currentVerifiedEmail}</div>
      </div>
    </menu>
  </button>
)

export const InviteButton: FunctionalComponent<{
  inviteUrl: string
  isTeamUser?: boolean
}> = ({ inviteUrl, isTeamUser }) => (
  <GlobalNavMenuButton
    icon='gift'
    label='Refer'
    onMouseEnter={() => (state.showInviteDialog = true)}
    onMouseLeave={() => (state.showInviteDialog = false)}
    updatedBadge
  >
    <ui-tooltip disablePointer bgColor='surface-upper' persist name='global-nav'>
      <menu class='flex flex-col items-center p-0 py-3 px-1 m-0 w-60 text-xs text-center rounded-lg spacing-y-3'>
        <InlineIconText icon='gift' class='text-xl font-extrabold text-brand uppercase'>
          Referral Code
        </InlineIconText>
        <p class='text-md text-center text-glyph-light'>
          Give {!isTeamUser && 'and get '}a month of Premium for free when people sign up with your link.
        </p>
        <section class='flex items-center self-stretch p-1 pl-2 bg-surface-base rounded spacing-x-1'>
          <input class='flex-grow text-glyph-lighter bg-transparent' type='text' readOnly value={inviteUrl} />
          <button onClick={() => copyText(inviteUrl)} class='p-1 px-2 font-bold text-surface-base bg-link rounded-sm'>
            Copy
            <ui-infotip>Only eligible for users outside of your organization</ui-infotip>
            <ui-tooltip on='click' bgColor='info-ok' anchor='bottom' slot='tooltip'>
              Copied
            </ui-tooltip>
          </button>
        </section>
        <section class='flex self-stretch -mt-8px spacing-x-8px'>
          <InlineIconText
            flip
            icon='twitter'
            iconClass='text-base'
            class='flex-grow justify-between px-2 pt-7px pb-9px text-md font-bold text-link normal-case rounded border cursor-pointer'
            onClick={() => {
              const url = new URL('https://twitter.com/intent/tweet')
              const text = [
                '@FathomDotVideo is a great new app for anyone like myself who’s on a lot of @Zoom meetings. It’s completely changed how I take notes. Saves me a ton of time and stress!',
                'Sign up with my referral link to skip the waitlist',
              ]
              url.searchParams.append('text', text.join('\n\n'))
              url.searchParams.append('url', inviteUrl)
              // eslint-disable-next-line
              window.open(url.toString(), '_blank')
            }}
          >
            Tweet
          </InlineIconText>

          <InlineIconText
            flip
            icon='linkedin'
            iconClass='text-base'
            class='flex-grow justify-between px-2 pt-7px pb-9px text-md font-bold text-link normal-case rounded border cursor-pointer'
            onClick={() => {
              const url = new URL('https://www.linkedin.com/sharing/share-offsite/')
              url.searchParams.append('url', inviteUrl)
              // eslint-disable-next-line
              window.open(url.toString(), '_blank')
            }}
          >
            LinkedIn
          </InlineIconText>
        </section>
        <div class='flex gap-2 items-center text-info-warn'>
          <ui-icon icon='coin-dollar' size='2xl' />
          <span class='text-left'>
            Earn commission on{' '}
            <a class='underline' href='/for/teams' target='_blank'>
              Team Edition
            </a>{' '}
            upgrades with our referral program.{' '}
            <a class='underline' href='/for/referral-program'>
              Learn More
            </a>
          </span>
        </div>
      </menu>
    </ui-tooltip>
  </GlobalNavMenuButton>
)
